import { useEffect, useRef } from "react";
import { UserIcon } from "@heroicons/react/20/solid";

import images from "../../assets/images";
import { Message } from "../../hooks/use-chat";
import { RenderAction } from "./message-actions/render-action";
import { RenderComponent } from "./message-actions/render-component";

interface MessagesProps {
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  extraSendMessage?: (msg: string) => void;
  messages: Message[];
  loading?: boolean;
  sendUserSignature: (signature: string) => Promise<void>;
  sendMessageHidden: (message: string) => void;
  accessToken: string | null;
  setError: React.Dispatch<React.SetStateAction<string>>;
  submit: (data: any) => void;
}

export default function Messages({
  setMessages,
  submit,
  messages,
  loading,
  sendUserSignature,
  sendMessageHidden,
  accessToken,
  setError
}: MessagesProps) {
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className="max-h-[90vh] h-full overflow-auto py-10 px-4 flex flex-col flex-1">
      <div
        id="chat"
        className="flex-col w-full overflow-auto pb max-w-5xl mx-auto"
      >
        {messages.map((msg, index) => (
          <div key={index} className="flex flex-row mb-6">
            <div className="min-w-8">
              <div
                className={`w-8 h-8 rounded-full flex justify-center items-center overflow-hidden ${msg.agent === "human" ? "bg-[#FF5650]" : "bg-green-500"
                  }`}
              >
                {msg.agent === "human" ? (
                  <div>
                    <UserIcon className="w-6 h-6 text-gray-900 dark:text-white" />
                  </div>
                ) : (
                  <img
                    alt="pfp"
                    src={images.SphereLogo}
                    style={{ width: 20, height: 20 }}
                  />
                )}
              </div>
            </div>
            <div className={`ml-3 max-w-screen-2xl`}>
              <span className="text-gray-900 dark:text-gray-100 font-medium">
                {msg.agent === "human" ? "You" : "Surface Area"}
              </span>
              <pre className="text-gray-900 dark:text-gray-300 text-sm mt-1 break-words whitespace-pre-wrap">
                {msg.agent === "human" ? (
                  msg.message
                ) : msg.agent === "ui" ? (
                  <RenderComponent
                    submit={submit}
                    setMessages={setMessages}
                    component={msg.component ?? "-"}
                    data={msg.data}
                    accessToken={accessToken as string}
                    setError={setError}
                    sendMessageHidden={sendMessageHidden}
                    sendUserSignature={sendUserSignature}
                  />
                ) : (
                  <RenderAction
                    message={msg.message}
                  />
                )}
              </pre>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex flex-row mb-6">
            <div className="min-w-8">
              <div
                className={`w-8 h-8 rounded-full flex justify-center items-center overflow-hidden bg-green-500`}
              >
                <img
                  alt="pfp"
                  src={images.SphereLogo}
                  style={{ width: 20, height: 20 }}
                />
              </div>
            </div>
            <div className={`ml-3 max-w-screen-2xl`}>
              <span className="text-gray-900 dark:text-gray-100">
                Surface Area
              </span>
              <div className="text-gray-900 dark:text-gray-300 mt-1 animate-pulse">
                Loading...
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
